/**
 * Custom hook for handling RMC persisted order initialization
 * Manages conversion of persisted orders to form data and contact information initialization
 * Includes date validation and reset functionality
 */

import {
  getSelectableDateRange,
  isDateValid
} from '@hconnect/tuc-processes/fields/dateTime/dateTime.utils'
import {useOrderPlacer} from '@hconnect/tuc-processes/fields/orderPlacer/useOrderPlacer'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import {useSiteContact} from '@hconnect/tuc-processes/fields/siteContact/useSiteContact'
import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import moment from 'moment-business-days'
import React from 'react'
import {useSelector} from 'react-redux'

import {AppState} from '../../../../Root.store'
import {getUserProfile} from '../../../../UserProfile/UserProfile.selectors'
import {
  extractContactInfoFromPersistedOrder,
  processPersistedOrders
} from '../services/rmcOrderPersistenceService'
import {RmcOrderPayload} from '../services/rmcOrderPlacementService'

import {useRmcBulkOrderEdit} from './useRmcBulkOrderEdit'

interface UseRmcPersistedOrderInitializationProps {
  persistedOrders?: RmcOrderPayload[]
  bulkOrderEdit: ReturnType<typeof useRmcBulkOrderEdit>
}

interface UseRmcPersistedOrderInitializationReturn {
  userProfileCountry: string
  isProcessingPersistentOrders: boolean
}

export const useRmcPersistedOrderInitialization = ({
  persistedOrders,
  bulkOrderEdit
}: UseRmcPersistedOrderInitializationProps): UseRmcPersistedOrderInitializationReturn => {
  const {setOrderPlacer} = useOrderPlacer()
  const {setSiteContact} = useSiteContact()
  const {getValues} = useOrderProcessFormContext()
  const truckConfiguration = useFieldConfigurationContext('trucks')
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')
  const mixConfiguration = useFieldConfigurationContext('mix')

  // Get quantity configuration to access the correct UOM and fullLoad options
  const {
    quantityConfiguration: {uom: quantityUom},
    fullLoadOptions
  } = useQuantity()

  // Get user profile country from Redux state
  const userProfileCountry = useSelector(
    (state: AppState) => getUserProfile(state).userProfile?.country ?? 'US'
  )

  // Use refs to track processing state to prevent duplicate processing
  const persistedOrdersProcessed = React.useRef<string | null>(null)
  const contactInfoInitialized = React.useRef(false)

  // Process persisted orders and create form orders
  React.useEffect(() => {
    // Early return if no persisted orders to process
    if (!persistedOrders || persistedOrders.length === 0) {
      persistedOrdersProcessed.current = null
      return
    }

    const {formDataArray, persistedOrdersId} = processPersistedOrders(
      persistedOrders,
      userProfileCountry,
      {
        quantityUom,
        fullLoadOptions,
        defaultFormData: getValues(),
        truckConfiguration
      }
    )

    // Only process if we haven't processed this exact set of orders before
    if (persistedOrdersProcessed.current !== persistedOrdersId) {
      // Track if any dates were reset
      let hasDateResets = false

      // Prepare orders from form data with date validation
      formDataArray.forEach((formData) => {
        const contract = mixConfiguration.contracts.find(
          (currentContract) => currentContract.contractNumber === formData.contract?.contractNumber
        )
        const {businessDays, businessHours} = dateTimeConfiguration

        if (formData.dateTime?.[0] && contract?.dateRange && businessDays && businessHours) {
          const {minDate, maxDate} = getSelectableDateRange(
            contract.dateRange,
            businessHours,
            businessDays
          )
          const orderDateTime = moment(formData.dateTime[0])

          // Check if the date is still valid
          if (!isDateValid(orderDateTime, minDate, maxDate, businessDays)) {
            // Date is no longer valid, reset to earliest available date
            formData.dateTime = [minDate.toISOString()]
            hasDateResets = true
          }
        }
      })

      void bulkOrderEdit.initializeOrders(formDataArray)

      // Set the date resets flag in the order edit state
      if (hasDateResets) {
        bulkOrderEdit.setHasDateResets(true)
      }

      // Mark these specific orders as processed
      persistedOrdersProcessed.current = persistedOrdersId
    }
    // Include date validation dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    persistedOrders,
    userProfileCountry,
    quantityUom,
    fullLoadOptions,
    getValues,
    truckConfiguration,
    dateTimeConfiguration,
    mixConfiguration
  ])

  // Initialize contact information from persisted orders
  React.useEffect(() => {
    // Early return if no persisted orders
    if (!persistedOrders || persistedOrders.length === 0) {
      contactInfoInitialized.current = false
      return
    }

    // Skip if already initialized
    if (contactInfoInitialized.current) {
      return
    }

    const contactInfo = extractContactInfoFromPersistedOrder(persistedOrders[0], userProfileCountry)

    if (contactInfo) {
      // Set order placer information if available
      if (contactInfo.orderPlacer) {
        setOrderPlacer(contactInfo.orderPlacer)
      }

      // Set site contact information if available
      if (contactInfo.siteContact) {
        setSiteContact(contactInfo.siteContact)
      }

      // Mark contact info as initialized
      contactInfoInitialized.current = true
    }
  }, [persistedOrders, setOrderPlacer, setSiteContact, userProfileCountry, quantityUom])

  return {
    userProfileCountry,
    isProcessingPersistentOrders:
      persistedOrdersProcessed.current === null && (persistedOrders?.length ?? 0) > 0
  }
}
